import { SideModal } from "../";
import { Button } from "../../shared";
import { Box, Chip, Typography } from "@mui/joy";
import { GitCompare } from "lucide-react";
import { SupplierComparison as PriceComparison } from "../Supplier/PriceComparison";
import { SupplierSelectionGrid } from "./components/SupplierSelectionGrid";
import { SelectedSuppliersDisplay } from "./components/SelectedSuppliersDisplay";
import { useSupplierComparisonLogic } from "./hooks/useSupplierComparisonLogic";
import type { ISupplierComparisonProps } from "./types";

export function SupplierComparison(props: ISupplierComparisonProps) {
    const { selectedValues, setIsNextStageEligible } = props;

    const {
        openSupplierComparison,
        comparisonResults,
        purchaseStrategy,
        selectedSuppliers,
        isComparing,
        supplierSearchQuery,
        savedSuppliersInfo,
        itemSupplierSelections,
        selectedSupplier,
        activeSuppliers,
        isLoadingSuppliers,
        canCompare,
        isChangingSelection,
        setOpenSupplierComparison,
        setSupplierSearchQuery,
        toggleSupplierSelection,
        handleOnCompare,
        handleClearSelection,
        handleItemSupplierSelect,
        handleSupplierConfirmation,
    } = useSupplierComparisonLogic({
        selectedValues,
        setIsNextStageEligible,
    });

    return (
        <Box>
            <Box mt={2}>
                <Box mb={2}>
                    <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600, mb: 0.5 }}>
                        Supplier Selection
                    </Typography>
                    <Typography level="body-xs" sx={{ color: "text.secondary" }}>
                        Choose suppliers to compare prices and make your selection
                    </Typography>
                </Box>

                {isChangingSelection ||
                (!selectedSupplier?.name && Object.keys(itemSupplierSelections).length === 0) ? (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                                sx={{
                                    pb: 1.5,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }}>
                                    Select Suppliers to Compare
                                </Typography>
                                {selectedSuppliers.length > 0 && (
                                    <Chip size="sm" variant="soft" color="primary">
                                        {selectedSuppliers.length}{" "}
                                        {selectedSuppliers.length === 1 ? "supplier" : "suppliers"} selected
                                    </Chip>
                                )}
                            </Box>

                            <SupplierSelectionGrid
                                suppliers={activeSuppliers}
                                selectedSuppliers={selectedSuppliers}
                                isLoading={isLoadingSuppliers}
                                searchQuery={supplierSearchQuery}
                                onSearchChange={setSupplierSearchQuery}
                                onToggleSelection={toggleSupplierSelection}
                            />
                        </Box>

                        <Box display="flex" alignItems="center" gap={2}>
                            <Button
                                disabled={!canCompare}
                                startDecorator={<GitCompare size={20} />}
                                onClick={handleOnCompare}
                                variant="soft"
                                color="primary"
                                loading={isComparing}
                            >
                                Compare & Choose
                            </Button>
                        </Box>
                    </>
                ) : (
                    <SelectedSuppliersDisplay
                        purchaseStrategy={purchaseStrategy}
                        selectedSupplier={selectedSupplier}
                        savedSuppliersInfo={savedSuppliersInfo}
                        itemSupplierSelections={itemSupplierSelections}
                        onClearSelection={handleClearSelection}
                    />
                )}
            </Box>

            <SideModal
                portalBoxProps={{
                    minWidth: "70%",
                    width: 1200,
                    maxWidth: "100vw",
                }}
                open={openSupplierComparison}
                onClose={() => setOpenSupplierComparison(false)}
            >
                <PriceComparison
                    comparisonResults={comparisonResults}
                    purchaseStrategy={purchaseStrategy}
                    onClose={() => setOpenSupplierComparison(false)}
                    onConfirm={handleSupplierConfirmation}
                    itemSupplierSelections={itemSupplierSelections}
                    onItemSupplierSelect={handleItemSupplierSelect}
                />
            </SideModal>
        </Box>
    );
}
